
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Ambassador, ApplicationStatus, SyncLog, AppSettings, User } from './types';
import { ApplicantCard } from './components/ApplicantCard';
import { SyncConsoleModal } from './components/SyncConsoleModal';
import { DetailModal } from './components/DetailModal';
import { SettingsModal } from './components/SettingsModal';
import { CreateAmbassadorModal } from './components/CreateAmbassadorModal';
import { Login } from './components/Login';

// Connection defaults
const DEFAULT_WORKER_SECRET = 'Ambasador_Secret_2024';

// Provided Airtable Field IDs
const AT_FIELDS = {
  CODE: 'fldlCGJ2n6yZ4Og8H',
  PASSWORD: 'fldh9FWQb3jPRXT6v'
};

const sanitizeForCode = (str: string) => str.toLowerCase().replace(/[^a-z0-9]/g, '');
const wrapWithProxy = (url: string, proxy: string) => `${proxy}${url}`;

export default function App() {
  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(() => sessionStorage.getItem('si_auth') === 'true');
  const [settings, setSettings] = useState<AppSettings>(() => {
    const saved = localStorage.getItem('si_settings');
    return saved ? JSON.parse(saved) : {
      typeformToken: '', formId: '', corsProxy: 'https://red-darkness-a642.kyle-3dc.workers.dev/',
      ghlToken: '', ghlLocationId: '', ghlAmbassadorCodeFieldId: '', ghlAmbassadorPasswordFieldId: '',
      shopifyUrl: '', shopifyAccessToken: '',
      workerUrl: localStorage.getItem('si_worker_url') || window.location.origin,
      workerSecret: localStorage.getItem('si_worker_secret') || DEFAULT_WORKER_SECRET,
      airtableApiKey: '', airtableBaseId: '', atReferralTableId: '', atAmbassadorTableId: ''
    };
  });

  const [ambassadors, setAmbassadors] = useState<Ambassador[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterStatus, setFilterStatus] = useState<ApplicationStatus | 'ALL'>(ApplicationStatus.NEW);
  const [isSyncing, setIsSyncing] = useState(false);
  const [isAutoSyncing, setIsAutoSyncing] = useState(false);
  const [isAirtableSyncing, setIsAirtableSyncing] = useState(false);
  const [isGhlSyncing, setIsGhlSyncing] = useState(false);
  const [isSettingsOpen, setIsSettingsOpen] = useState(false);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [syncLogs, setSyncLogs] = useState<SyncLog[]>([]);
  const [isSyncComplete, setIsSyncComplete] = useState(false);
  const [selectedAmbassador, setSelectedAmbassador] = useState<Ambassador | null>(null);

  const handleUpdateConnection = useCallback((url: string, secret: string) => {
    setSettings(prev => {
      if (prev.workerUrl === url && prev.workerSecret === secret) return prev;
      return { ...prev, workerUrl: url, workerSecret: secret };
    });
  }, []);

  const addLog = (message: string, type: 'info' | 'success' | 'error' = 'info') => {
    setSyncLogs(prev => [...prev, { timestamp: new Date().toLocaleTimeString(), message, type }]);
    console.log(`[${type.toUpperCase()}] ${message}`);
  };

  const saveToCloud = async (endpoint: string, data: any) => {
    try {
      const workerBase = (settings.workerUrl || window.location.origin).replace(/\/+$/, '');
      const res = await fetch(`${workerBase}/${endpoint}`, {
        method: 'POST',
        headers: { 'Authorization': settings.workerSecret, 'Content-Type': 'application/json' },
        body: JSON.stringify(data)
      });
      if (!res.ok) {
        const errorText = await res.text();
        console.warn(`Cloud save to /${endpoint} failed: ${res.status} ${errorText}`);
      } else {
        console.log(`Cloud save to /${endpoint} successful.`);
      }
    } catch (err) {
      console.error(`Worker error while saving /${endpoint}:`, err);
    }
  };

  const fetchData = useCallback(async () => {
    const activeUrl = settings.workerUrl || window.location.origin;
    if (!isAuthenticated || !activeUrl) return;

    try {
      const headers = { 'Authorization': settings.workerSecret };
      const workerBase = activeUrl.replace(/\/+$/, '');
      
      console.log("Fetching cloud data from:", workerBase);
      
      // Fetch Ambassadors
      const ambRes = await fetch(`${workerBase}/ambassadors`, { headers });
      if (ambRes.ok) {
        const data = await ambRes.json();
        if (Array.isArray(data)) setAmbassadors(data);
      }

      // Fetch Settings
      const setRes = await fetch(`${workerBase}/settings`, { headers });
      if (setRes.ok) {
        const cloudSettings = await setRes.json();
        console.log("Cloud settings received:", cloudSettings);
        if (cloudSettings && Object.keys(cloudSettings).length > 0) {
          setSettings(prev => {
            const merged = { 
              ...prev, 
              ...cloudSettings,
              // Never let the cloud overwrite the actual connection to the cloud
              workerUrl: prev.workerUrl, 
              workerSecret: prev.workerSecret 
            };
            localStorage.setItem('si_settings', JSON.stringify(merged));
            return merged;
          });
        }
      }
    } catch (err) {
      console.error("Critical error fetching cloud data:", err);
    }
  }, [isAuthenticated, settings.workerUrl, settings.workerSecret]);

  const silentSync = useCallback(async () => {
    if (isAutoSyncing) return;
    setIsAutoSyncing(true);
    try {
      const workerBase = (settings.workerUrl || window.location.origin).replace(/\/+$/, '');
      const res = await fetch(`${workerBase}/sync`, {
        method: 'POST',
        headers: { 'Authorization': settings.workerSecret }
      });
      if (res.ok) {
        await fetchData(); 
      }
    } catch (e) {
      console.error("Background sync failed:", e);
    } finally {
      setIsAutoSyncing(false);
    }
  }, [settings.workerUrl, settings.workerSecret, fetchData, isAutoSyncing]);

  useEffect(() => { 
    if (isAuthenticated) {
      fetchData(); 
      silentSync();
    }
  }, [isAuthenticated, settings.workerUrl, fetchData]);

  const checkCodeInAirtable = async (code: string): Promise<boolean> => {
    if (!settings.airtableApiKey || !settings.airtableBaseId || !settings.atAmbassadorTableId) return false;
    const baseUrl = `https://api.airtable.com/v0/${settings.airtableBaseId}/${settings.atAmbassadorTableId}`;
    const formula = `LOWER({${AT_FIELDS.CODE}}) = '${code.toLowerCase()}'`;
    const encodedFormula = encodeURIComponent(formula);
    const searchUrl = wrapWithProxy(`${baseUrl}?filterByFormula=${encodedFormula}`, settings.corsProxy);

    try {
      const res = await fetch(searchUrl, {
        headers: { 'Authorization': `Bearer ${settings.airtableApiKey}` }
      });
      const data = await res.json();
      return (data.records && data.records.length > 0);
    } catch (err) {
      console.error("Airtable Uniqueness Check Error:", err);
      return false;
    }
  };

  const handleApprove = async (id: string, quiet = false) => {
    const ambassador = ambassadors.find(a => a.id === id);
    if (!ambassador) return;
    if (!quiet) { setSyncLogs([]); setIsAirtableSyncing(true); setIsSyncComplete(false); }
    
    addLog(`>>> APPROVING: ${ambassador.firstName}`);
    try {
      if (!settings.airtableApiKey) throw new Error("Airtable API Key missing.");
      
      let finalCode = sanitizeForCode(ambassador.firstName);
      let isTaken = await checkCodeInAirtable(finalCode);
      
      if (isTaken) {
        addLog(`Code '${finalCode}' taken. Trying First + Last Initial...`);
        finalCode = sanitizeForCode(ambassador.firstName + ambassador.lastName.charAt(0));
        isTaken = await checkCodeInAirtable(finalCode);
      }

      let counter = 2;
      const baseCandidate = finalCode;
      while (isTaken) {
        finalCode = `${baseCandidate}${counter}`;
        addLog(`Code collision. Trying '${finalCode}'...`);
        isTaken = await checkCodeInAirtable(finalCode);
        counter++;
        if (counter > 50) throw new Error("Could not generate a unique code after 50 attempts.");
      }

      addLog(`Unique Code secured: ${finalCode}`, 'success');
      const finalPassword = `${finalCode}${Math.floor(10 + Math.random() * 90)}`;

      const baseUrl = `https://api.airtable.com/v0/${settings.airtableBaseId}/${settings.atAmbassadorTableId}`;
      const createRes = await fetch(wrapWithProxy(baseUrl, settings.corsProxy), {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${settings.airtableApiKey}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          records: [{ fields: { [AT_FIELDS.CODE]: finalCode, [AT_FIELDS.PASSWORD]: finalPassword } }],
          typecast: true
        })
      });
      if (!createRes.ok) throw new Error(`Airtable creation failed.`);
      addLog(`Airtable record created.`, 'success');

      if (settings.ghlToken) {
        const interestField = ambassador.additionalFields.find(f => f.label === 'Interest');
        const interestValue = interestField?.value || '';
        
        let ghlTag = "amb_welcome"; 
        if (interestValue === "I'm a Surrogacy Is Agency surrogate") {
          ghlTag = "amb_welcome";
        } else if (interestValue === "Surrogacy Is helped me find my surrogacy agency") {
          ghlTag = "amb_welcome";
        } else if (interestValue === "Neither, but I'd like to be a SurroSwag Affiliate") {
          ghlTag = "swagaff_welcome";
        }

        const customFields = [];
        if (settings.ghlAmbassadorCodeFieldId) customFields.push({ id: settings.ghlAmbassadorCodeFieldId, value: finalCode });
        if (settings.ghlAmbassadorPasswordFieldId) customFields.push({ id: settings.ghlAmbassadorPasswordFieldId, value: finalPassword });

        await fetch(`${settings.corsProxy}https://services.leadconnectorhq.com/contacts/upsert`, {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${settings.ghlToken}`, 'Version': '2021-07-28', 'Content-Type': 'application/json' },
          body: JSON.stringify({
            email: ambassador.email,
            firstName: ambassador.firstName,
            lastName: ambassador.lastName,
            locationId: settings.ghlLocationId,
            tags: [ghlTag],
            customFields
          })
        });
        addLog(`HighLevel processed with tag: ${ghlTag}`, 'success');
      }

      const updated = { ...ambassador, status: ApplicationStatus.WELCOME_SENT, ambassadorCode: finalCode, password: finalPassword };
      await saveToCloud('ambassadors', updated);
      setAmbassadors(prev => prev.map(a => a.id === id ? updated : a));
      addLog(`Database updated locally and on cloud.`, 'success');

    } catch (e: any) { addLog(`Error: ${e.message}`, 'error'); } finally { if (!quiet) setIsSyncComplete(true); }
  };

  const handleArchive = async (id: string) => {
    const amb = ambassadors.find(a => a.id === id);
    if (!amb) return;
    const updated = { ...amb, status: ApplicationStatus.ARCHIVED };
    setAmbassadors(prev => prev.map(a => a.id === id ? updated : a));
    await saveToCloud('ambassadors', updated);
    if (selectedAmbassador?.id === id) setSelectedAmbassador(updated);
  };

  const handleReverseApproval = async (id: string) => {
    const amb = ambassadors.find(a => a.id === id);
    if (!amb) return;
    let newStatus = ApplicationStatus.NEW;
    if (amb.status === ApplicationStatus.SIGNED_ONBOARDED) {
      newStatus = ApplicationStatus.WELCOME_SENT;
    } else if (amb.status === ApplicationStatus.WELCOME_SENT || amb.status === ApplicationStatus.ARCHIVED) {
      newStatus = ApplicationStatus.NEW;
    }
    const updated = { ...amb, status: newStatus };
    setAmbassadors(prev => prev.map(a => a.id === id ? updated : a));
    await saveToCloud('ambassadors', updated);
    if (selectedAmbassador?.id === id) setSelectedAmbassador(updated);
  };

  const startSync = async () => {
    if (isSyncing) return;
    setIsSyncing(true); setSyncLogs([]); setIsSyncComplete(false);
    addLog(`Initiating manual synchronization...`);
    try {
      const workerBase = (settings.workerUrl || window.location.origin).replace(/\/+$/, '');
      const res = await fetch(`${workerBase}/sync`, {
        method: 'POST',
        headers: { 'Authorization': settings.workerSecret }
      });
      if (!res.ok) throw new Error("Worker sync request failed.");
      const result = await res.json();
      addLog(`Worker processed ${result.count || 0} records.`, 'success');
      await fetchData();
      addLog(`Sync complete. List updated.`, 'success');
    } catch (e: any) { addLog(e.message, 'error'); } finally { setIsSyncComplete(true); }
  };

  const handleOnboard = async (id: string) => {
    const amb = ambassadors.find(a => a.id === id);
    if (!amb) return;
    setIsGhlSyncing(true); setSyncLogs([]); setIsSyncComplete(false);
    addLog(`>>> FINAL ONBOARDING: ${amb.firstName}`);
    
    try {
      if (settings.ghlToken) {
        const interestField = amb.additionalFields.find(f => f.label === 'Interest');
        const interestValue = interestField?.value || '';

        let onboardTag = "siamb_signed";
        if (interestValue === "I'm a Surrogacy Is Agency surrogate") {
          onboardTag = "agencyamb_signed";
        } else if (interestValue === "Surrogacy Is helped me find my surrogacy agency") {
          onboardTag = "siamb_signed";
        } else if (interestValue === "Neither, but I'd like to be a SurroSwag Affiliate") {
          onboardTag = "swagaff_signed";
        }

        const customFields = [];
        if (settings.ghlAmbassadorCodeFieldId) customFields.push({ id: settings.ghlAmbassadorCodeFieldId, value: amb.ambassadorCode });
        if (settings.ghlAmbassadorPasswordFieldId) customFields.push({ id: settings.ghlAmbassadorPasswordFieldId, value: amb.password });

        await fetch(`${settings.corsProxy}https://services.leadconnectorhq.com/contacts/upsert`, {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${settings.ghlToken}`, 'Version': '2021-07-28', 'Content-Type': 'application/json' },
          body: JSON.stringify({ 
            email: amb.email, 
            locationId: settings.ghlLocationId, 
            tags: [onboardTag],
            customFields
          })
        });
        addLog(`GHL Tagged: ${onboardTag} and Credentials synced`, 'success');
      }

      if (settings.shopifyUrl && settings.shopifyAccessToken) {
        const storeUrl = settings.shopifyUrl.replace(/\/+$/, '');
        const headers = { 'X-Shopify-Access-Token': settings.shopifyAccessToken, 'Content-Type': 'application/json' };
        let customer: any = null;

        addLog(`Checking Shopify for ${amb.email}...`);
        const eSearch = await fetch(wrapWithProxy(`${storeUrl}/admin/api/2024-01/customers/search.json?query=email:${amb.email}`, settings.corsProxy), { headers });
        const eData = await eSearch.json();
        if (eData.customers?.length > 0) customer = eData.customers[0];

        if (!customer && amb.phone && amb.phone !== 'N/A') {
          addLog(`Email not found. Checking phone: ${amb.phone}...`);
          const pSearch = await fetch(wrapWithProxy(`${storeUrl}/admin/api/2024-01/customers/search.json?query=phone:${amb.phone}`, settings.corsProxy), { headers });
          const pData = await pSearch.json();
          if (pData.customers?.length > 0) customer = pData.customers[0];
        }

        // --- NEW: Map Interest to Ambassador Tier ---
        const interestField = amb.additionalFields.find(f => f.label === 'Interest');
        const interestValue = interestField?.value || '';
        let ambassadorTier = 'Ambassador'; // Default

        if (interestValue === "Surrogacy Is helped me find my surrogacy agency") {
          ambassadorTier = 'SI Ambassador';
        } else if (interestValue === "I'm a Surrogacy Is Agency surrogate") {
          ambassadorTier = 'Agency Ambassador';
        } else if (interestValue === "Neither, but I'd like to be a SurroSwag Affiliate") {
          ambassadorTier = 'Swag Ambassador';
        }
        addLog(`Mapped Ambassador Tier: ${ambassadorTier}`, 'info');

        const noteDump = [
          `--- AMBASSADOR APPLICATION ---`,
          `Name: ${amb.firstName} ${amb.lastName}`,
          `Tier: ${ambassadorTier}`,
          `Code: ${amb.ambassadorCode || 'Pending'}`,
          `Socials: IG: ${amb.instagramHandle || 'N/A'}, TT: ${amb.tiktokHandle || 'N/A'}`,
          `Background: ${amb.reason}`,
          `--- DETAILS ---`,
          ...amb.additionalFields.map(f => `${f.label}: ${f.value}`),
          `Address: ${amb.address?.street}, ${amb.address?.city}, ${amb.address?.state}, ${amb.address?.zip}`
        ].join('\n');

        const metafields = [
          {
            namespace: "custom",
            key: "ambassador_code",
            value: amb.ambassadorCode || '',
            type: "single_line_text_field"
          },
          {
            namespace: "custom",
            key: "ambassador_password",
            value: amb.password || '',
            type: "single_line_text_field"
          },
          {
            namespace: "custom",
            key: "ambassador_tier",
            value: ambassadorTier,
            type: "single_line_text_field"
          }
        ];

        if (customer) {
          const tags = Array.from(new Set([...(customer.tags?.split(', ') || []), 'ambassador'])).join(', ');
          await fetch(wrapWithProxy(`${storeUrl}/admin/api/2024-01/customers/${customer.id}.json`, settings.corsProxy), {
            method: 'PUT', headers, body: JSON.stringify({ customer: { id: customer.id, tags, note: noteDump, metafields } })
          });
          addLog(`Shopify customer updated with tags, notes, and tier: ${ambassadorTier}.`, 'success');
        } else {
          await fetch(wrapWithProxy(`${storeUrl}/admin/api/2024-01/customers.json`, settings.corsProxy), {
            method: 'POST', headers, body: JSON.stringify({
              customer: { 
                first_name: amb.firstName, 
                last_name: amb.lastName, 
                email: amb.email, 
                phone: amb.phone !== 'N/A' ? amb.phone : undefined, 
                tags: 'ambassador', 
                note: noteDump,
                metafields
              }
            })
          });
          addLog(`New Shopify customer created with full credentials and tier: ${ambassadorTier}.`, 'success');
        }
      }

      const updated = { ...amb, status: ApplicationStatus.SIGNED_ONBOARDED };
      await saveToCloud('ambassadors', updated);
      setAmbassadors(prev => prev.map(a => a.id === id ? updated : a));
      addLog(`Status finalized. Persistent storage updated.`, 'success');

    } catch (e: any) { addLog(e.message, 'error'); } finally { setIsSyncComplete(true); }
  };

  const filteredAmbassadors = useMemo(() => {
    return ambassadors
      .filter(a => {
        const matchStatus = filterStatus === 'ALL' || a.status === filterStatus;
        const matchSearch = (a.firstName + ' ' + a.lastName + ' ' + a.email).toLowerCase().includes(searchQuery.toLowerCase());
        return matchStatus && matchSearch;
      })
      .sort((a, b) => {
        const dateA = new Date(a.appliedDate).getTime();
        const dateB = new Date(b.appliedDate).getTime();
        return dateB - dateA;
      });
  }, [ambassadors, filterStatus, searchQuery]);

  const stats = useMemo(() => ({
    all: ambassadors.length,
    new: ambassadors.filter(a => a.status === ApplicationStatus.NEW).length,
    welcome: ambassadors.filter(a => a.status === ApplicationStatus.WELCOME_SENT).length,
    onboarded: ambassadors.filter(a => a.status === ApplicationStatus.SIGNED_ONBOARDED).length,
  }), [ambassadors]);

  if (!isAuthenticated) return <Login onLoginSuccess={() => { setIsAuthenticated(true); sessionStorage.setItem('si_auth', 'true'); fetchData(); }} workerUrl={settings.workerUrl} workerSecret={settings.workerSecret} onUpdateConnection={handleUpdateConnection} />;

  return (
    <div className="min-h-screen bg-purple-50 flex flex-col font-sans">
      <header className="sticky top-0 z-50 glass-header border-b border-purple-100 px-8 py-5">
        <div className="max-w-[1500px] mx-auto flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div className="flex items-center gap-4">
            <div className="w-10 h-10 bg-purple-600 rounded-2xl flex items-center justify-center text-white shadow-lg shadow-purple-100 relative group">
               <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24"><path d="M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z"/></svg>
               {isAutoSyncing && (
                 <div className="absolute -top-1 -right-1 w-3 h-3 bg-purple-400 rounded-full border-2 border-white animate-ping"></div>
               )}
            </div>
            <div>
              <div className="flex items-center gap-2">
                <h1 className="text-2xl font-bold text-purple-900 leading-none">Onboarding 3.0</h1>
                {isAutoSyncing && (
                  <span className="text-[8px] font-black uppercase text-purple-400 animate-pulse mt-1 tracking-tighter">Updating Database...</span>
                )}
              </div>
              <p className="text-[10px] font-black uppercase tracking-widest text-purple-400 mt-1">Ambassador Portal</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <input type="text" placeholder="Search ambassadors..." value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} className="bg-white border border-gray-100 rounded-2xl px-10 py-2.5 text-xs outline-none w-64 transition-all focus:ring-2 focus:ring-purple-200" />
            <button onClick={() => setIsSettingsOpen(true)} className="p-3 bg-white border border-gray-100 rounded-xl text-gray-400 hover:text-purple-600 shadow-sm transition-colors"><svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" /></svg></button>
            <button onClick={() => setIsCreateModalOpen(true)} className="bg-black text-white rounded-full px-6 py-2.5 text-[10px] font-bold uppercase tracking-widest hover:bg-gray-800 transition-colors">New Record</button>
            <button onClick={startSync} className="bg-purple-600 text-white rounded-full px-6 py-2.5 text-[10px] font-bold uppercase tracking-widest shadow-lg shadow-purple-100 transition-all hover:bg-purple-700">Sync Typeform</button>
          </div>
        </div>
      </header>

      <main className="flex-1 p-8 max-w-[1500px] mx-auto w-full pb-32">
        <section className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
          {[{l:'Pending',v:stats.new,c:'text-purple-600',s:ApplicationStatus.NEW},{l:'Welcomed',v:stats.welcome,c:'text-blue-600',s:ApplicationStatus.WELCOME_SENT},{l:'Onboarded',v:stats.onboarded,c:'text-green-600',s:ApplicationStatus.SIGNED_ONBOARDED},{l:'Total Records',v:stats.all,c:'text-gray-900',s:'ALL'}].map((x,i)=>(
            <button key={i} onClick={()=>setFilterStatus(x.s as any)} className={`bg-white p-8 rounded-3xl border transition-all text-left ${filterStatus===x.s?'border-purple-600 ring-4 ring-purple-100 shadow-md':'border-gray-100 hover:border-purple-200'}`}><p className="text-[10px] font-black uppercase text-purple-400 mb-2">{x.l}</p><div className={`text-4xl font-bold ${x.c}`}>{x.v}</div></button>
          ))}
        </section>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {filteredAmbassadors.length === 0 ? (
            <div className="col-span-3 py-20 text-center text-gray-400 italic">No records found. Click Sync Typeform to fetch new data.</div>
          ) : (
            filteredAmbassadors.map(a => (
              <ApplicantCard 
                key={a.id} 
                ambassador={a} 
                onClick={setSelectedAmbassador} 
                onApprove={handleApprove} 
                onOnboard={handleOnboard} 
                onArchive={handleArchive} 
                onReverseApproval={handleReverseApproval} 
              />
            ))
          )}
        </div>
      </main>

      <SyncConsoleModal isOpen={isSyncing || isGhlSyncing || isAirtableSyncing} logs={syncLogs} isComplete={isSyncComplete} onClose={() => { setIsSyncing(false); setIsGhlSyncing(false); setIsAirtableSyncing(false); fetchData(); }} />
      <DetailModal ambassador={selectedAmbassador} onClose={() => setSelectedAmbassador(null)} onApprove={handleApprove} onOnboard={handleOnboard} onArchive={handleArchive} onReverseApproval={handleReverseApproval} onUpdateAmbassador={(u)=>{saveToCloud('ambassadors',u);setAmbassadors(p => p.map(a => a.id === u.id ? u : a));}} onUpdatePhoto={()=>{}} />
      <SettingsModal 
        isOpen={isSettingsOpen} 
        onClose={()=>setIsSettingsOpen(false)} 
        settings={settings} 
        onSave={async (s)=>{
          setSettings(s);
          localStorage.setItem('si_settings',JSON.stringify(s));
          await saveToCloud('settings', s);
          await fetchData();
        }} 
        users={[]} onAddUser={()=>{}} onUpdateUser={()=>{}} onDeleteUser={()=>{}} onTestTypeform={()=>{}} onTestGHL={()=>{}} onTestShopify={()=>{}} onTestWorker={()=>{}} onRepairDatabase={()=>{}} onDeleteAllRecords={()=>{}} onViewLogs={()=>{}} setConfirmConfig={()=>{}} 
      />
      <CreateAmbassadorModal isOpen={isCreateModalOpen} onClose={()=>setIsCreateModalOpen(false)} onCreate={(n)=>{saveToCloud('ambassadors',n);setAmbassadors(p => [n, ...p]);}} />
    </div>
  );
}
